import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from "react";
import './scss/Content.scss';
import {Routes, Route, Outlet, Link, useLocation, useParams, useNavigate} from "react-router-dom";
import {SupportNeedsView} from "./views/SupportNeedsView";
import {ReactComponent as Logo} from "./svg/logo-green.svg";
import {ReactComponent as CloseIcon} from "./svg/close.svg";

import SupportRequest from "./views/SupportRequest";
import {useAuthData, useCurrentUserData} from "./App";
import {LocaleSwitcher} from "./LocaleContext";
import {useIntl} from "react-intl";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import Unsubscribe from "./views/Unsubscribe";
import {GroupView} from "./views/GroupView";
import {PopupElement, PopupProvider, Snackbar} from "./UIElements";
import {ChartNoAxesColumn, ExternalLink, Send} from "lucide-react";

export default function Content({userGroupData}) {
    const location = useLocation()
    //const [transitionName, setTransitionName] = useState("next")
    const nodeRef = React.useRef()
    const [linkState, setLinkState] = useState({
        direction: location.state?.direction || "none",
        animation: location.state?.animation || "false"
    })

    useEffect(() => {
        setLinkState({
            direction: location?.state?.direction || "none",
            animation: location?.state?.animation || "false"
        })
        // Return to previous scroll position on /requests page
        if (location?.pathname === '/' || location?.pathname.endsWith('/requests')) {
            const scrollPosition = sessionStorage.getItem("scrollPosition")
            if (scrollPosition) {
                window.scrollTo(0, parseInt(scrollPosition))
                sessionStorage.removeItem("scrollPosition")
            }
        } else if (location?.state?.animation === "true") {
            // Scroll to top to avoid funky y-coordinate sliding upon animation
            window.scrollTo(0, 0)
        }
    }, [location.state])

    const direction = location.state?.direction || linkState.direction
    const shouldAnimate = location.state?.animation || linkState.animation

    const transitionName = direction === 'right' ? 'prev' : linkState?.direction === 'left' ? 'next' : 'none'
    const timeoutValue = shouldAnimate === "true" ? 400 : 0

    const {currentUserData} = useCurrentUserData()
    const showGroupView = currentUserData?.groupView

    return <TransitionGroup component={null}>
        <CSSTransition
            key={location.key}
            classNames={transitionName}
            timeout={timeoutValue}
            nodeRef={nodeRef}>
            <div ref={nodeRef}>
                <Routes location={location}>
                    <Route path="/" element={<Contexts />}>
                        {showGroupView ?
                            <>
                                <Route index element={<GroupView />} />
                                <Route path={'group'} element={<GroupView />}>
                                    <Route path={':surveyId'} element={<GroupView />} />
                                </Route>
                                <Route path={'request/:requestId'} element={<SupportRequest />}/>
                                <Route path={'requests/request/:requestId'} element={<SupportRequest />}/>
                                <Route path={'requests'} element={<SupportNeedsView />} />
                                <Route path={'*'} element={<GroupView/>} />
                            </> :
                            <>
                                <Route index element={<SupportNeedsView />} />
                                <Route path={'request/:requestId'} element={<SupportRequest />}/>
                                <Route path={'*'} element={<SupportNeedsView />} />
                            </>
                        }
                        <Route path={'unsubscribe'} element={<Unsubscribe />} />
                    </Route>
                </Routes>
            </div>
        </CSSTransition>
    </TransitionGroup>
}

const ToastContext = createContext({
    toastContext: {},
    setToastContext: () => {}
})

function ToastProvider({children, toastContext}) {
    const [toastData, setToastData] = useState(toastContext)
    const value = useMemo( () => (
        {toastData, setToastData}
        ),[toastData])
    return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export function useToastData() {
    const context = useContext(ToastContext)
    if (context === undefined) {
        throw new Error('useToastData must be used within an CurrentToastProvider')
    }
    return context
}

function Contexts() {
    return <PopupProvider>
        <SnackbarWrapper>
            <Layout />
        </SnackbarWrapper>
        <PopupElement />
    </PopupProvider>
}

const SnackbarContext = createContext({
    show: false,
    text: "",
    state: "", //error, warning, info, success
    autoHide: true
})

function SnackbarWrapper({children}) {
    const [snackbarData, setSnackbarData] = useState(useContext(SnackbarContext))
    const value = useMemo(() => (
        {snackbarData, setSnackbarData}
    ), [snackbarData])

    return <SnackbarContext.Provider value={value}>
        {children}
        <Snackbar show={snackbarData.show} text={snackbarData.text} state={snackbarData.state} autoHide={snackbarData.autoHide} />
    </SnackbarContext.Provider>
}

export function useSnackbar() {
    const context = useContext(SnackbarContext)
    if (context === undefined) {
        throw new Error('useSnackbar must be used inside SnackbarWrapper')
    }
    return context
}

function Layout() {
    const {currentUserData} = useCurrentUserData()
    const showNavigator = currentUserData?.groupView
    const location = useLocation()
    const fromLocation = location?.state?.from?.pathname || "/"
    const navigateToGroup = (showNavigator && fromLocation.startsWith("/group")) || (showNavigator && location?.pathname.startsWith("/group"))

    return <div className={"content"}>
        <div className={"header"}>
            <Link className={"logo"} to={!showNavigator ? "/" : navigateToGroup ? "/group" :"/requests"}>
                <Logo />
            </Link>
            <Profile />
        </div>
        {showNavigator &&
            <ViewNavigator />
        }
        <div className={"main"}>
            <Outlet />
        </div>
    </div>
}

function ViewNavigator() {
    const intl = useIntl()
    const location = useLocation()
    const pathname = location?.pathname
    const inGroupView = pathname && pathname.startsWith("/group")

    return <div className={"view-navigator-container"}>
        <div className={"view-navigator"}>
            <Link className={inGroupView ? "active" : ""} to={"/group"}>
                {intl.formatMessage({
                    id:"groupOverview",
                    defaultMessage:"Group Overview"
                })}
            </Link>
            <Link  className={!inGroupView ? "active" : ""} to={"/requests"}>
                {intl.formatMessage({
                    id:"mySupportRequests",
                    defaultMessage:"My Support Requests"
                })}
            </Link>
        </div>
    </div>
}

function Toast() {
    const {toastData, setToastData} = useToastData()
    const closeable = toastData.closeable
    const toastType = toastData.type  // generic, error, warning, success, info, hint
    const displayToast = toastData.display
    const toastText = toastData.text

    const hideToast = () => {
        setToastData({display: false, closeable: false, type:"generic", text:""})
    }

    if (displayToast) {
        return <div className={"toast " + toastType}>
            <p>{toastText}</p>
            {closeable &&
                <button onClick={()=>hideToast()} className={"close"}><CloseIcon/></button>
            }
        </div>
    }
    return null
}

export function Profile() {
    const {currentUserData} = useCurrentUserData()
    const {authData} = useAuthData()
    const firstName = currentUserData && currentUserData?.meta && currentUserData?.meta.hasOwnProperty("firstname") ? currentUserData.meta.firstname : null
    const lastName = currentUserData && currentUserData?.meta && currentUserData?.meta.hasOwnProperty("lastname") ? currentUserData.meta.lastname : null
    const userid = currentUserData?.meta?.userid ? currentUserData.meta.userid : (currentUserData?.meta?.email ? currentUserData.meta.email : authData.uid)
    const initials = (firstName && lastName) ? firstName.charAt(0) + lastName.charAt(0) : userid.substring(0, 2)
    const [showProfilePopover, setShowProfilePopover] = useState(false)
    const popoverRef = useRef(null)
    const popoverTriggerRef = useRef(null)
    const intl = useIntl()
    const logoutURL = authData.hasOwnProperty("logoutURL") ? authData.logoutURL : null

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target) && !popoverTriggerRef.current.contains(event.target) ) {
                setShowProfilePopover(false)
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[popoverRef])

    const externalNavItems = [
        {
            path: "/stats",
            icon: <ChartNoAxesColumn size={16} strokeWidth={1.5} />,
            messageId: "app.stats",
            defaultMessage: "Stats"
        },
        {
            path: "/admin",
            icon: <Send size={16} strokeWidth={1.5} />,
            messageId: "app.admin",
            defaultMessage: "Admin Console",
            superuser: true
        }
    ].filter(nav => !nav.superuser || currentUserData?.superuser)

    const baseUrl = `${window.location.protocol}//${window.location.hostname}`

    return <div className={"profile-container"}>
        <button className={"profile-icon"} ref={popoverTriggerRef} onClick={()=>setShowProfilePopover(!showProfilePopover)}>{initials}</button>
        {showProfilePopover &&
        <div className={"profile-popover"} ref={popoverRef}>
            <>
                <h2>
                    {(firstName && lastName) ?
                        firstName +" "+ lastName :
                        userid
                    }
                </h2>
                <hr/>
            </>

            <h4>
                {intl.formatMessage({
                    id: 'profile',
                    defaultMessage: 'Profile',
                })}
            </h4>
            <p>{userid}</p>
            <hr/>
            <h4>
                {intl.formatMessage({
                    id: 'language',
                    defaultMessage: 'Language',
                })}
            </h4>
            <LocaleSwitcher />
            <hr/>
            {externalNavItems.map((item, index) => (
                <a
                    href={`${baseUrl}${item.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={"external-link"}
                    key={index}
                >
                    {item.icon}
                    <span>{intl.formatMessage({ id: item.messageId, defaultMessage: item.defaultMessage })}</span>
                    <ExternalLink size={12} strokeWidth={1.5} style={{ marginLeft: 'auto' }} />
                </a>
            ))}
            {logoutURL &&
                <>
                    <hr/>
                    <a href={logoutURL}>
                        {intl.formatMessage({
                            id: 'logOut',
                            defaultMessage: 'Log out',
                        })}
                    </a>
                </>
            }

        </div>
        }
    </div>
}